'use client';

import clsx from 'clsx';
import React, { ReactElement } from 'react';

import Modal from '@/components/modal/modal';

import styles from './multiStepModal.module.scss';


type Foo = {
  filter?: () => boolean;
  validate: () => boolean;
};

export type MultiStepModalProps<RetObj = object> = {
  cb: ( ret: RetObj ) => void;
  onClose: () => void;
};

/**
 * @property intStyles are styles set in the implementation class
 */
export type MultiStepModalState<StepConstType, InputConstType> = {
  intStyles?: string; // used to set styles within the implementation class constructor
  step: StepConstType[ keyof StepConstType ];
  steps: StepConstType[ keyof StepConstType ][];
  validationErrKeys: ( InputConstType[ keyof InputConstType ] )[];
};

/**
 * Abstract base class for creating multi-step modals with validation and step management.
 *
 * @template P - Props type for the modal component.
 * @template S - State type for the modal component.
 * @template StepConstType - Record type representing the possible steps.
 * @template InputConstType - Record type representing the possible inputs.
 * @template RetObj - Return object type for the callback.
 *
 * @remarks
 * - Extend this class to implement a multi-step modal with custom steps and input validation.
 * - Implements step navigation, input validation, and completion handling.
 * - Requires implementation of `INPUT_CONSTS`, `STEP_CONSTS`, `inputsByStep`, `onComplete`, and `stepper`.
 *
 * @property {InputConstType} INPUT_CONSTS - Abstract constant representing input keys.
 * @property {StepConstType} STEP_CONSTS - Abstract constant representing step keys.
 * @property {Record} inputsByStep - Abstract mapping of step to input validation objects.
 *
 * @method inputRequiredCheck - Checks if an input is missing and returns a required style if so.
 * @method inputValidator - Validates required inputs for the current step, updates state, and throws if missing.
 * @method validateInput - Validates a single input for the current step; useful for manual or event-driven validation.
 * @method step - Advances to the next step or calls onComplete if finished; resets validation errors.
 * @method stepper - Abstract method to render the stepper UI.
 * @method render - Renders the modal with the stepper.
 *
 * @example
 * ```tsx
 * class MyModal extends MultiStepModal<{}, {}, typeof STEP_CONSTS, typeof INPUT_CONSTS, MyReturnType> {
 *   // Implement required abstract members...
 * }
 * ```
 */
export abstract class MultiStepModal<
  P extends object,
  S extends object,
  StepConstType extends Record<string, string | number | symbol>,
  InputConstType extends Record<string, string | number | symbol>,
  RetObj extends object
> extends React.Component<
  P & MultiStepModalProps<RetObj>,
  S & MultiStepModalState<StepConstType, InputConstType>
  > {
  constructor( props: P & MultiStepModalProps<RetObj> ) {
    super( props );
  }

  abstract INPUT_CONSTS: InputConstType;
  abstract STEP_CONSTS: StepConstType;

  abstract inputsByStep: {
    [ step in typeof this.STEP_CONSTS[ keyof typeof this.STEP_CONSTS ] ]: {
      [ input in typeof this.INPUT_CONSTS[ keyof typeof this.INPUT_CONSTS ] ]?: Foo;
    };
  };

  inputRequiredCheck( input: typeof this.INPUT_CONSTS[ keyof typeof this.INPUT_CONSTS ] ): undefined | string {
    const isMissing = this.state.validationErrKeys.includes( input );

    if( isMissing ) return styles.required;
  };

  async inputValidator() {
    const missingRequiredInputs: ( typeof this.INPUT_CONSTS[ keyof typeof this.INPUT_CONSTS ] )[] = [];

    for( const [ key, input ] of Object.entries<Foo | undefined>( this.inputsByStep[ this.state.step ] ) ) {
      if( input?.filter ) {
        const relevant = input.filter();
        if( !relevant ) continue;
      }

      if( input?.validate() ) missingRequiredInputs.push( key as ( InputConstType[ keyof InputConstType ] ) );
    }

    if( missingRequiredInputs.length ) {
      this.setState( prevState => ( {
        ...prevState,
        validationErrKeys: missingRequiredInputs
      } ) );
      throw Error( 'missing inputs: ' + missingRequiredInputs.join( ', ' ) );
    }
  };

  abstract onComplete(): void;

  /**
   * Validates a single input for the current step and updates `validationErrKeys` accordingly.
   *
   * Useful for manually triggering validation on a specific field, such as in event handlers
   * (e.g. `onChange`). Unlike `inputValidator`, which validates all inputs for the current step
   * at once, this method targets a single input key.
   *
   * If the input has a `filter`, validation is skipped when the filter returns false.
   * The input key is added to `validationErrKeys` if invalid, or removed if now valid.
   *
   * @param inputKey - The value from `INPUT_CONSTS` identifying the input to validate.
   *
   * @example
   * ```tsx
   * onChange={ e => {
   *   this.setState( { name: e.target.value }, async() => {
   *     await this.validateInput( this.INPUT_CONSTS.NAME );
   *   } );
   * } }
   * ```
   */
  validateInput( inputKey: InputConstType[ keyof InputConstType ] ) {
    const stepInputsObj = this.inputsByStep[ this.state.step ];

    if( !stepInputsObj ) return;

    const input = stepInputsObj[ inputKey ];

    if( !input ) return;

    if( input.filter ) {
      const relevant = input.filter();

      if( !relevant ) return;
    }

    const isInvalid = input.validate();

    this.setState( prevState => {
      const errKeys = prevState.validationErrKeys.filter( k => k !== inputKey );

      if( isInvalid ) errKeys.push( inputKey );

      return { ...prevState, validationErrKeys: errKeys };
    } );
  };

  async step() {
    this.setState( prevState => ( {
      ...prevState,
      validationErrKeys: []
    } ) ); // reset

    try {
      await this.inputValidator();
    } catch( e ) {
      console.info( ( e as Error ).message );
      return;
    }

    const {
      step,
      steps
    } = this.state;

    const stepsCount = steps.length;
    const currStepNo = steps.findIndex( val => val === step );
    const nextStepNo = currStepNo + 1;

    if( nextStepNo === stepsCount ) {
      this.onComplete();
    } else {
      this.setState( prevState => ( {
        ...prevState,
        step: prevState.steps[ nextStepNo ]
      } ) );
    }
  };

  stepBack() {
    const {
      step,
      steps
    } = this.state;

    const currStepNo = steps.findIndex( val => val === step );
    const prevStepNo = currStepNo - 1;
    if( prevStepNo < 0 ) return;
    this.setState( prevState => ( {
      ...prevState,
      step: prevState.steps[ prevStepNo ]
    } ) );
  };

  abstract stepper(): ReactElement;

  render() {
    const { intStyles } = this.state;

    return (
      <Modal className={ clsx( styles.modal, !!intStyles && intStyles ) }>
        { this.stepper() }
      </Modal>
    );
  }
}